/* Load only near the viewport; respect reduced-motion preferences. */
if (!customElements.get('il-capsule')) customElements.define('il-capsule', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = {signal:this.abort.signal};
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.video = this.querySelector('video');
    this.failed = false;
    this.visible = false;
    this.pending = false;
    this.video.muted = true;
    this.motion.addEventListener('change', () => this.sync(), options);
    document.addEventListener('visibilitychange', () => this.sync(), options);
    this.video.addEventListener('playing', () => {
      if (!this.shouldPlay()) {this.video.pause();return;}
      this.classList.add('is-ready');
      if (this.dataset.alpha === 'true') this.checkAlpha();
    }, options);
    this.video.addEventListener('error', () => {
      this.failed=true;this.classList.remove('is-ready');this.sync();
    }, options);
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.sync();
    }, {threshold:.1});
    this.observer.observe(this);
    this.sync();
  }
  shouldPlay() {
    return this.isConnected && this.visible && !this.motion.matches && !document.hidden && !this.failed;
  }
  sync() {
    if (!this.isConnected) return;
    this.classList.toggle('is-reduced',this.motion.matches);
    if (!this.shouldPlay()) {this.video.pause();return;}
    if (!this.video.getAttribute('src')) {
      // Prefer VP9 WebM where the browser supports it; MP4 covers the rest.
      // WebKit plays VP9 but ignores its alpha channel, so a transparent clip is
      // served to WebKit as HEVC instead, which is the format Safari carries alpha in.
      const webm = this.video.dataset.srcWebm;
      const hevc = this.video.dataset.srcHevc;
      const canWebm = webm && this.video.canPlayType('video/webm; codecs="vp9"');
      const ua = navigator.userAgent;
      const isWebKit = /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua);
      let src;
      if (isWebKit && hevc && this.video.canPlayType('video/mp4; codecs="hvc1"')) src = hevc;
      else if (canWebm) src = webm;
      else src = this.video.dataset.src;
      if (!src) return;
      this.video.src = src;
    }
    if (this.pending || !this.video.paused) return;
    this.pending=true;
    const signal=this.abort.signal;
    this.video.play().catch(() => {
      // Autoplay restrictions retain the still poster without adding player UI.
      if (!signal.aborted && this.shouldPlay()) {
        this.failed=true;this.classList.remove('is-ready');this.sync();
      }
    }).finally(() => {
      if (!signal.aborted) this.pending=false;
    });
  }
  checkAlpha() {
    // Read one frame back: if every pixel decodes opaque the browser dropped the
    // alpha channel, so the tile is told to fall back to its flat matte colour.
    if (this.alphaChecked) return;
    this.alphaChecked = true;
    try {
      const size = 8;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext('2d', {willReadFrequently:true});
      if (!ctx) return;
      ctx.clearRect(0,0,size,size);
      ctx.drawImage(this.video,0,0,size,size);
      const data = ctx.getImageData(0,0,size,size).data;
      for (let i = 3; i < data.length; i += 4) if (data[i] < 250) return;
      this.classList.add('il-capsule--opaque');
    } catch (error) {
      // A tainted or unreadable canvas tells us nothing; keep the textured tile.
    }
  }
  disconnectedCallback() {
    this.abort?.abort();this.observer?.disconnect();this.video?.pause();
    this.pending=false;
  }
});
